export type StoreType =
  | "FASHION_DESIGNER"
  | "FOOD_RESTAURANT"
  | "PHARMACY"
  | "GADGET_REPAIR";

export interface ReceiptMetadata {
  style?: string;
  pickupDate?: string;
  tokenNo?: string;
  itemsSummary?: string;
  deviceModel?: string;
  imei?: string;
  jobScope?: string;
  repairStatus?: string;
  estTime?: string;
}

export interface ReceiptTransaction {
  id: string;
  storeName: string;
  storePhone: string;
  customerName: string;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
  paymentMethod: string;
  timestamp: string;
  metadata: ReceiptMetadata;
}

const templates: Record<StoreType, (t: ReceiptTransaction) => string> = {
  FASHION_DESIGNER: (t) =>
    `${t.storeName} Order Confirmation 🧵
Hello ${t.customerName}, your custom design order has been successfully logged into production!

🧾 Order Ref: ${t.id}
👗 Style: ${t.metadata.style ?? "N/A"}
📅 Pickup Date: ${t.metadata.pickupDate ?? "TBD"}
💵 Total Cost: ₦${t.totalAmount.toLocaleString()}
✅ Deposit Paid: ₦${t.amountPaid.toLocaleString()}
🛑 Balance Due: ₦${t.balanceDue.toLocaleString()} (To be paid on or before pickup)

Thank you for choosing us to style you! Need to change measurements? Call ${t.storePhone}.`,

  FOOD_RESTAURANT: (t) =>
    `${t.storeName} Order Packed! 🍲
Your order is ready and paid.

🧾 Token No: ${t.metadata.tokenNo ?? "N/A"}
🕒 Time: ${t.timestamp}
🍽️ Meal: ${t.metadata.itemsSummary ?? "N/A"}
💵 Paid: ₦${t.amountPaid.toLocaleString()} (${t.paymentMethod})

🔥 Packed fresh and hot. Enjoy your meal! For catering inquiries, call ${t.storePhone}.`,

  PHARMACY: (t) =>
    `${t.storeName} Dispensation Record 💊

🧾 Rx Ref: ${t.id}
💊 Medications: ${t.metadata.itemsSummary ?? "N/A"}
💰 Total Paid: ₦${t.totalAmount.toLocaleString()}

📝 Note: Please follow the dosage instructions written on your drug pack. Keep out of reach of children. Call ${t.storePhone} for pharmacist guidance.`,

  GADGET_REPAIR: (t) =>
    `${t.storeName} Service Invoice 📱

🧾 Job ID: ${t.id}
📱 Device: ${t.metadata.deviceModel ?? "N/A"}
🔍 IMEI/Serial: ${t.metadata.imei ?? "N/A"}
🔧 Job Scope: ${t.metadata.jobScope ?? "N/A"}
🛠️ Status: ${t.metadata.repairStatus ?? "N/A"} (Est. Ready: ${t.metadata.estTime ?? "TBD"})
💰 Total Cost: ₦${t.totalAmount.toLocaleString()}
✅ Amount Paid: ₦${t.amountPaid.toLocaleString()} (${t.paymentMethod})

⚠️ Keep this message safe. You must present this Job ID to claim your device. For repair updates, call ${t.storePhone}.`,
};

export function generateReceipt(
  transaction: ReceiptTransaction,
  storeType: StoreType
): string {
  const fn = templates[storeType];
  if (!fn) return "Unknown store type.";
  return fn(transaction);
}

export const RECEIPT_STORE_TYPE_LABELS: Record<StoreType, string> = {
  FASHION_DESIGNER: "Fashion Designer / Tailor",
  FOOD_RESTAURANT:  "Food & Restaurant",
  PHARMACY:         "Pharmacy & Chemist",
  GADGET_REPAIR:    "Gadgets & Repair",
};

export const SAMPLE_TRANSACTIONS: Record<StoreType, ReceiptTransaction> = {
  FASHION_DESIGNER: {
    id: "ORD-2025-0047",
    storeName: "Vogue Stitch Atelier",
    storePhone: "08012345678",
    customerName: "Amaka Okafor",
    totalAmount: 65000,
    amountPaid: 25000,
    balanceDue: 40000,
    paymentMethod: "Transfer",
    timestamp: "28 Jun 2025, 11:32 AM",
    metadata: {
      style: "Ankara A-line gown with peplum top",
      pickupDate: "12 Jul 2025",
    },
  },
  FOOD_RESTAURANT: {
    id: "TXN-0821",
    storeName: "Mama's Kitchen",
    storePhone: "08098765432",
    customerName: "Chukwuemeka Eze",
    totalAmount: 4500,
    amountPaid: 4500,
    balanceDue: 0,
    paymentMethod: "POS",
    timestamp: "28 Jun 2025, 1:14 PM",
    metadata: {
      tokenNo: "A-019",
      itemsSummary: "Jollof rice + Chicken + Zobo (Large)",
    },
  },
  PHARMACY: {
    id: "RX-20250628-003",
    storeName: "HealthPlus Chemist",
    storePhone: "07011223344",
    customerName: "Fatima Aliyu",
    totalAmount: 8750,
    amountPaid: 8750,
    balanceDue: 0,
    paymentMethod: "Cash",
    timestamp: "28 Jun 2025, 9:50 AM",
    metadata: {
      itemsSummary:
        "Amoxicillin 500mg ×14, Paracetamol 500mg ×10, Vitamin C 1000mg ×30",
    },
  },
  GADGET_REPAIR: {
    id: "JOB-2025-0193",
    storeName: "TechFix Hub",
    storePhone: "09055667788",
    customerName: "Biodun Adeyemi",
    totalAmount: 22000,
    amountPaid: 22000,
    balanceDue: 0,
    paymentMethod: "Transfer",
    timestamp: "28 Jun 2025, 3:05 PM",
    metadata: {
      deviceModel: "Samsung Galaxy A54",
      imei: "356784012345678",
      jobScope: "Screen replacement + battery swap",
      repairStatus: "In Progress",
      estTime: "2 Jul 2025",
    },
  },
};
